"use client";

import React, { useState, useMemo, useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { Autocomplete, TextField } from "@mui/material";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import useLocationSearch from "@/hooks/tour-search/use-location-search";
import { locationFiltersFactory } from "@/factories/search-filters/location-filter-factory";
import { LocationFilter } from "@/types/dto/search-filters/location-filter";

// Extend Window interface for agent status
declare global {
  interface Window {
    colletteAgentStatus?: {
      isAuthenticated: boolean;
      isAgent: boolean;
    };
  }
}

const AutoCompleteLocationSearchBar = () => {
  // DataLayer push function
  const pushDataLayer = (location: LocationFilter) => {
    if (typeof window !== "undefined") {
      if (!window.dataLayer) {
        window.dataLayer = [];
      }
      window.dataLayer.push({
        locationSearch: {
          locationId: location.id,
          locationName: location.displayName,
          locationValue: location.value,
          interaction: "hero_search"
        }
      });
    }
  };
  const router = useRouter();
  const t = useTranslations("AUTOCOMPLETE");
  const [inputValue, setInputValue] = useState("");
  const [currentValues, setCurrentValues] = useState<string[]>([]);
  const [resultCount, setResultCount] = useState(0);

  // Get agent status from global window object set by HomepageLayoutClient
  const getAgentStatus = () => {
    if (typeof window !== "undefined" && window.colletteAgentStatus) {
      const { isAuthenticated, isAgent } = window.colletteAgentStatus;
      return {
        isAuthenticated,
        isAgent,
        isAgentString: isAuthenticated && isAgent ? "true" : "false"
      };
    }
    return {
      isAuthenticated: false,
      isAgent: false,
      isAgentString: "false"
    };
  };

  // Use the location search hook
  const { data: locationData, isLoading, error } = useLocationSearch();

  // Transform the API data to match the expected format using the factory
  const transformedLocations = useMemo(() => {
    if (!locationData?.Locations) return [];

    return locationFiltersFactory(locationData.Locations);
  }, [locationData]);

  // Filter options based on input
  const filteredOptions = useMemo(() => {
    if (!inputValue) return transformedLocations;
    return transformedLocations.filter((option: LocationFilter) =>
      option.displayName.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [inputValue, transformedLocations]);

  // Get options with loading and error states
  const getOptions = () => {
    if (isLoading) {
      return [{ displayName: t("Loading destinations"), id: "loading", value: "", fieldName: "" }];
    }

    if (error) {
      return [{ displayName: t("Error loading destinations"), id: "error", value: "", fieldName: "" }];
    }

    if (filteredOptions.length > 0) {
      return filteredOptions;
    }

    return [{ displayName: t("Sorry no destinations match your search"), id: "no-match", value: "", fieldName: "" }];
  };

  // Handler for selection
  const handleLocationChange = (selected: Record<string, string>) => {
    setCurrentValues((prev) => [...prev, ...Object.values(selected)]);
  };

  // Handler for autocomplete selection and navigation
  const handleAutocompleteChange = (_: any, value: string | LocationFilter | null) => {
    // If no match, do nothing
    if (!value || typeof value === "string") return;
    handleLocationChange({ [value.fieldName]: value.value });
    pushDataLayer(value);
    if (window.Wistia) {
      window._wq = [];
    }
    
    const valueFieldName = value.fieldName === "Keyword" ? "keyword" : value.fieldName;
    router.push(
      `/find-your-tour?currentPage=1&${valueFieldName}=${encodeURIComponent(value.value)}&interaction=hero_search`
    );
  };

  // Handler for Enter key press
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      // Get the filtered options
      const options = getOptions();
      // Check if there's exactly one valid option (not loading, error, or no-match)
      const validOptions = options.filter(
        (opt) => opt.id !== "loading" && opt.id !== "error" && opt.id !== "no-match"
      );

      if (validOptions.length === 1 && inputValue.trim().length > 0) {
        const option = validOptions[0] as LocationFilter;
        handleAutocompleteChange(null, option);
      }
    }
  };

  // Log visible autocomplete results to the console whenever inputValue or filteredOptions change
  useEffect(() => {
    // Wait for the DOM to update
    setTimeout(() => {
      const listbox = document.querySelector("ul[id^='location-where-listbox']");
      if (listbox) {
        const items = Array.from(listbox.querySelectorAll("li"));
        const names = items.map(li => li.textContent?.trim()).filter(Boolean);
        setResultCount(names.length);
      } else {
        setResultCount(0);
      }
    }, 0);
  }, [inputValue, filteredOptions]);

  return (
    <Autocomplete
      data-search-interaction="hero"
      data-testid="location-search-autocomplete"
      data-results={resultCount}
      data-is-agent={getAgentStatus().isAgentString}      
      freeSolo
      id="location-where"
      disableClearable
      options={getOptions()}
      getOptionLabel={(option: string | LocationFilter) =>
        typeof option === "string"
          ? option
          : option.displayName
      }
      filterOptions={(options) => options} // <-- Prevent MUI from filtering again
      isOptionEqualToValue={(option, value) =>
        typeof option !== "string" &&
        typeof value !== "string" &&
        option.id === value.id
      }
      getOptionDisabled={(option) => {
        if (typeof option === "string") return false;
        // Disable loading, error, and no-match options
        return option.id === "loading" || option.id === "error" || option.id === "no-match" || currentValues.includes(option.value);
      }}
      renderOption={(props, option) => {
        const key = typeof option === "string" ? `custom-${option}` : option.id ?? option.displayName;
        const displayName = typeof option === "string" ? option : option.displayName;
        // Underline the matching part, except for loading, error, and no-match
        if (typeof option !== "string" && (option.id === "loading" || option.id === "error" || option.id === "no-match")) {
          return (
            <li {...props} key={key} style={{ color: "var(--theme-palette-text-primary)", opacity:"1" }}>
              {displayName}
            </li>
          );
        }
        let content: React.ReactNode = displayName;
        if (inputValue.length >= 1 && typeof option !== "string" && option.id && !["loading", "error", "no-match"].includes(option.id)) {
          const regex = new RegExp(`(${inputValue})`, "ig");
          const parts = displayName.split(regex);
          let partCounter = 0;
          content = parts.map((part) => {
            const isMatch = new RegExp(`^${inputValue}$`, "i").test(part);
            const uniqueKey = `${option.id}-${isMatch ? "underline" : "plain"}-${partCounter}`;
            partCounter += 1;
            return isMatch ? (
              <span key={uniqueKey} style={{ textDecoration: "underline" }}>{part}</span>
            ) : (
              <span key={uniqueKey}>{part}</span>
            );
          });
        }
        return (
          <li {...props} key={key} style={{ whiteSpace: "pre-wrap" }}>
            {content}
          </li>
        );
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={handleAutocompleteChange}
      renderInput={(params) => (
        <TextField
          {...params}
          data-testid="location-search-input"
          placeholder={t("Where do you want to go")}
          onKeyDown={handleKeyDown}
          slotProps={{
            input: {
              ...params.InputProps,
              inputProps: {
                ...params.inputProps,
                type: "search",
                "data-search-source": "hero"
              },
              startAdornment: (
                <>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "8px",
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: "var(--theme-palette-info-dark)"
                    }}
                  >
                    <SearchIcon sx={{ color: "text.primary", fontSize: 24 }} />
                  </span>
                  {params.InputProps.startAdornment}
                </>
              )
            }
          }}
          sx={{
            "& input::placeholder": {
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "24px",
              letterSpacing: "0.5px",
              opacity: 1
            }
          }}
        />
      )}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "background.default",
            maxHeight: "360px",
            overflowY: "auto",
            margin: "8px auto",
            width: "343px",
            borderRadius: "20px",
            boxShadow: 6
          }
        },
        popper: {
          modifiers: [
            {
              name: "flip",
              enabled: false
            }
          ]
        },
        listbox: {
          sx: {
            maxHeight: "360px",
            backgroundColor: "background.default",
            padding: 0,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            "& li": {
              padding: "14px 20px !important",
              "&.Mui-focused": {
                ":hover": { background: (theme) => `${theme.palette.divider} !important` }
              }
            }
          }
        }
      }}
      sx={{
        display: "flex",
        width: 360,
        height: 56,
        minWidth: 360,
        maxWidth: 720,
        alignItems: "center",
        gap: "4px",
        borderRadius: "28px",
        backgroundColor: "background.warm_strong",
        "& .MuiInputBase-root": {
          borderRadius: "28px",
          background: "transparent",
          height: 56
        },
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none"
        },
        "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: "none"
        }
      }}
    />
  );
};

export default AutoCompleteLocationSearchBar;